import type { Failure } from "@/modules/shared/failure";
import { renderFieldErrors } from "@/modules/shared/fieldCopy";
import { SYSTEM_ERROR, type FormState } from "@/modules/shared/forms";
import type { HoursSummary } from "./calendarCore";

/**
 * The **web's** copy for the Scheduling commands, and the one place a `Failure`
 * becomes a `FormState` (R22 T5).
 *
 * A separate module from the `"use server"` adapter because every export in one
 * of those must be an async function, so a pure lookup could neither live nor
 * be unit-tested there. Every sentence is unchanged from before the cores
 * existed.
 */
export function schedulingCopyFor(failure: Failure): string {
  switch (failure.code) {
    case "practitioner_not_found":
      // Scheduling's own wording: this surface is about who can be scheduled,
      // not about who exists, and Catalog says it differently for the same code
      // on the screen where somebody is removed from the team.
      return "This practitioner is no longer available for scheduling.";
    case "blackout_not_found":
      return "That time off is already gone.";
    case "capacity_below_one":
      return "An hour holds at least one appointment.";
    case "malformed_input":
      // Reached only when a form posts without an id it should always carry.
      // Field-level rejections never arrive here: they carry `fieldErrors` and
      // `formStateFor` renders them inline instead.
      return SYSTEM_ERROR;
    default:
      return SYSTEM_ERROR;
  }
}

/**
 * Turn a core's `Failure` into the `FormState` a screen renders. Anything
 * carrying `fieldErrors` belongs under the input that caused it; everything
 * else is a banner.
 */
export function formStateFor(
  failure: Failure,
  values?: Record<string, string>,
): FormState {
  if (failure.fieldErrors) {
    return { fieldErrors: renderFieldErrors(failure.fieldErrors), values };
  }
  return { formError: schedulingCopyFor(failure), values };
}

/**
 * The saved-hours banner (ClinicOpeningHoursScreen).
 *
 * **It states the consequence rather than the action**, because "saved" is not
 * the thing anybody needed to know: what a clinic wants confirmed is how much
 * of the week it just opened, and that existing bookings were left alone. The
 * all-closed case says so plainly rather than reporting zero hours as though it
 * were a number, since closing everything is legitimate and its consequence is
 * the part worth reading.
 */
export function hoursSavedMessage(summary: HoursSummary): string {
  if (summary.hours === 0) {
    return "Your clinic is now closed every hour. Nobody can be booked.";
  }
  const days = `${summary.days} ${summary.days === 1 ? "day" : "days"}`;
  const hours = `${summary.hours} ${summary.hours === 1 ? "hour" : "hours"}`;
  return `Saved. Open ${days}, ${hours} a week. Existing bookings keep their times.`;
}
